import { useCallback, useEffect, useRef, useState } from "react";
import {
  getDropiIntegrations,
  getIntegrationsAvailability,
} from "../../../services/integrations/integrationsService";
import {
  getAllPaymentMethods,
  getPaymentMethodConfiguration,
} from "../../../services/payment/payment";

import {
  IntegrationUIConfig,
  IntegrationType,
  INTEGRATIONS_UI_CONFIG,
} from "../config/integrations.config";
import { IntegrationAvailabilityApi } from "../config/integrations.types";
import { resolveAvailableIntegrations } from "../helpers/resolveAvailableIntegrations";
import { PaymentMethodStatus } from "../../payment/PaymentMethods/PaymentMethodsInterface";
import { DropiIntegrationInterface } from "../../../services/integrations/integrationsService";
import { Country } from "../../../const/countryList";
import { getWhatsAppAccount } from "../../../services/whatsapp/whatsappService";
import { decodeTokenService } from "../../../services/decodeTokenService";
import { getMetaIntegration } from "../../../services/meta/metaService";
import { userExperimentsService } from "../../../services/userExperiments/userExperimentsService";
import { getConfioIntegration } from "../../../services/confio/confioService";
import { isChatEnabledForOwner } from "../../../utils/chatAccess";
import { cachedRequest } from "../../../utils/requestCache";

const INTEGRATIONS_TTL_MS = 60_000;

const INTEGRATION_FLAG_MAP: Partial<Record<IntegrationType, string>> = {
  meta: "meta_campaigns_module",
  confio: "confio_payments_integration",
};

type ExperimentsResponse = Awaited<ReturnType<typeof userExperimentsService.getExperiments>>;
type IntegrationCardState = IntegrationUIConfig & {
  isActive: boolean;
  isConnected: boolean;
  alreadyConfigured: boolean;
};

const integrationsSnapshot = new Map<string, IntegrationCardState[]>();

const getIntegrationsCacheKey = (country: Country, ownerId: string | null) =>
  `integrations:${ownerId || "anonymous"}:${country}`;

const isFlagEnabled = (flagKey: string, experiments: ExperimentsResponse | null): boolean => {
  return experiments?.experiments?.[flagKey]?.enabled === "true";
};

const isIntegrationVisible = (id: IntegrationType, experiments: ExperimentsResponse | null): boolean => {
  const flag = INTEGRATION_FLAG_MAP[id];
  if (!flag) return true;
  return isFlagEnabled(flag, experiments);
};

export const clearIntegrationsSnapshot = () => {
  integrationsSnapshot.clear();
};

export const useIntegrations = (country: Country = Country.COLOMBIA) => {
  const ownerId = decodeTokenService.getUserIdFromToken();
  const cacheKey = getIntegrationsCacheKey(country, ownerId);
  const snapshot = integrationsSnapshot.get(cacheKey);
  const [integrationsDropi, setIntegrationsDropi] =
    useState<DropiIntegrationInterface>();
  const methodsStatus = useRef<PaymentMethodStatus[]>([]);
  const [loading, setLoading] = useState(!snapshot);
  const [integrations, setIntegrations] = useState<IntegrationCardState[]>(snapshot ?? []);

  const resolveIntegrationState = useCallback((
    type: IntegrationType,
    dropiData?: DropiIntegrationInterface
  ) => {
    if (type === "dropi") {
      const dropiIntegration = dropiData ?? integrationsDropi;
      const isActive = dropiIntegration?.is_active ?? false;
      return {
        isActive,
        isConnected: isActive,
        alreadyConfigured: isActive,
      };
    }

    const method = methodsStatus.current.find((m) => m.id === type);
    const active = method?.active ?? false;
    const alreadyConfigured = method?.already_configured ?? false;

    return {
      isActive: active && alreadyConfigured,
      isConnected: active && alreadyConfigured,
      alreadyConfigured,
    };
  }, [integrationsDropi]);

  useEffect(() => {
    const load = async () => {
      if (!integrationsSnapshot.get(cacheKey)) {
        setLoading(true);
      }

      try {
        const results = await cachedRequest(cacheKey, INTEGRATIONS_TTL_MS, () =>
          Promise.allSettled([
            getIntegrationsAvailability(),
            getDropiIntegrations(),
            getAllPaymentMethods(),
            getPaymentMethodConfiguration(),
            ownerId ? getWhatsAppAccount(ownerId) : Promise.reject("no owner"),
            getMetaIntegration(),
            userExperimentsService.getExperiments(),
            import.meta.env.VITE_DISABLED_CONFIO_PAYMENTS === "false"
              ? getConfioIntegration()
              : Promise.resolve(null),
          ])
        );

        const availabilityApi = results[0].status === "fulfilled"
          ? results[0].value
          : null;

        const dropiIntegration = results[1].status === "fulfilled"
          ? results[1].value
          : undefined;

        const paymentConfig = results[3].status === "fulfilled"
          ? results[3].value
          : [];

        const whatsappAccount = results[4].status === "fulfilled" ? results[4].value : null;
        const metaIntegration = results[5].status === "fulfilled" ? results[5].value : null;
        const experiments = results[6].status === "fulfilled" ? results[6].value : null;
        const confioIntegration = results[7].status === "fulfilled" ? results[7].value : null;

        results.forEach((result, index) => {
          if (result.status === "rejected") {
            const endpointNames = [
              "getIntegrationsAvailability",
              "getDropiIntegrations",
              "getAllPaymentMethods",
              "getPaymentMethodConfiguration",
              "getWhatsAppAccount",
              "getMetaIntegration",
              "getExperiments",
              "getConfioIntegration",
            ] as const;
            const error = result.reason;
            const is404 = error?.response?.status === 404;

            if (is404) {
              console.warn(`${endpointNames[index]} returned 404 (expected for some users)`);
            } else {
              console.error(`Error in ${endpointNames[index]}:`, error);
            }
          }
        });

        if (dropiIntegration) {
          setIntegrationsDropi(dropiIntegration);
        }

        methodsStatus.current = paymentConfig;

        const resolveStateFor = (id: IntegrationType) => {
          if (id === "meta") {
            const active = metaIntegration?.is_active ?? false;
            return { isActive: active, isConnected: active, alreadyConfigured: active };
          }
          if (id === "confio") {
            const connected = confioIntegration?.connected ?? false;
            const active = confioIntegration?.active ?? false;
            return { isActive: connected && active, isConnected: connected, alreadyConfigured: connected };
          }
          return resolveIntegrationState(id, dropiIntegration);
        };

        const dbIntegrations = availabilityApi
          ? resolveAvailableIntegrations({
              apiIntegrations: availabilityApi as IntegrationAvailabilityApi[],
              country,
            })
              .filter((integration) => isIntegrationVisible(integration.id, experiments))
              .map((integration) => ({
                ...INTEGRATIONS_UI_CONFIG[integration.id],
                ...resolveStateFor(integration.id),
                type: integration.id,
              }))
          : [];

        const visibleTypes = new Set(dbIntegrations.map((integration) => integration.type));
        const extraCards: IntegrationCardState[] = [];

        if (isChatEnabledForOwner(ownerId) && !visibleTypes.has("whatsapp")) {
          const whatsappIsActive = whatsappAccount?.is_active ?? false;
          extraCards.push({
            ...INTEGRATIONS_UI_CONFIG.whatsapp,
            isActive: whatsappIsActive,
            isConnected: whatsappIsActive,
            alreadyConfigured: !!whatsappAccount,
          });
        }

        if (!visibleTypes.has("meta") && isIntegrationVisible("meta", experiments)) {
          const active = metaIntegration?.is_active ?? false;
          extraCards.push({
            ...INTEGRATIONS_UI_CONFIG.meta,
            isActive: active,
            isConnected: active,
            alreadyConfigured: active,
          });
        }

        if (
          import.meta.env.VITE_DISABLED_CONFIO_PAYMENTS === "false" &&
          !visibleTypes.has("confio") &&
          isIntegrationVisible("confio", experiments)
        ) {
          const connected = confioIntegration?.connected ?? false;
          const active = confioIntegration?.active ?? false;
          extraCards.push({
            ...INTEGRATIONS_UI_CONFIG.confio,
            isActive: connected && active,
            isConnected: connected,
            alreadyConfigured: connected,
          });
        }

        const nextIntegrations = [...dbIntegrations, ...extraCards];
        integrationsSnapshot.set(cacheKey, nextIntegrations);
        setIntegrations(nextIntegrations);
      } catch (error) {
        console.error("Unexpected error loading integrations:", error);
        setIntegrations([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [cacheKey, country, ownerId, resolveIntegrationState]);

  return {
    integrations,
    loading,
  };
};
